import React, { useEffect, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import { staffMasterApi, labelOf, GENDERS, WORKER_TYPES, COLLARS, ROLL_STATUSES, PAY_TYPES } from '@/services/staffMasterApi';

const FIELDS = {
  staff_name: 'Name', designation: 'Designation', section: 'Section', dob: 'Date of Birth', doj: 'Date of Joining',
  gender: 'Gender', worker_type: 'Worker Type', collar: 'Collar', agency: 'Agency', pay_type: 'Pay Type', fixed_pay: 'Fixed Pay Amount', roll_status: 'In Roll / Left', left_date: 'Left Date', phone: 'Phone', essl_code: 'eSSL Code',
  email: 'Email', address: 'Address',
};
const LISTS = { gender: GENDERS, worker_type: WORKER_TYPES, collar: COLLARS, roll_status: ROLL_STATUSES, pay_type: PAY_TYPES };
const SOURCE = { create: ['Created', 'bg-emerald-100 text-emerald-700'], edit: ['Edited', 'bg-sky-100 text-sky-700'], csv_import: ['CSV import', 'bg-violet-100 text-violet-700'], transfer: ['Transfer', 'bg-amber-100 text-amber-800'] };

const inr = (v) => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const show = (field, v) => (!v ? '—' : field === 'fixed_pay' && !Number.isNaN(Number(v)) ? inr(v) : LISTS[field] ? labelOf(LISTS[field], v) || v : v);
const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });

const Entry = ({ h, i }) => {
  const [label, cls] = SOURCE[h.source] || [h.source, 'bg-gray-100 text-gray-700'];
  return (
    <div className="py-2 border-b last:border-0" data-testid={`staff-history-entry-${i}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded-full px-2 py-0.5 font-semibold ${cls}`} data-testid={`staff-history-source-${i}`}>{label}</span>
        <span className="font-medium text-gray-800" data-testid={`staff-history-by-${i}`}>{h.changed_by_name || 'Unknown'}</span>
        <span className="text-gray-500">{when(h.changed_at)}</span>
        {h.file && <span className="text-gray-400 truncate max-w-[180px]">{h.file}</span>}
        {h.effective_date && <span className="text-gray-500">effective {h.effective_date}{h.reason ? ` — ${h.reason}` : ''}</span>}
      </div>
      <ul className="mt-1 space-y-0.5">
        {h.changes.map((c) => (
          <li key={c.field} className="text-sm text-gray-700" data-testid={`staff-history-change-${i}-${c.field}`}>
            <span className="text-gray-500">{FIELDS[c.field] || c.field}:</span>{' '}
            <span className="line-through decoration-gray-400 text-gray-400">{show(c.field, c.old)}</span>{' → '}
            <span className="font-medium">{show(c.field, c.new)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Change history of one staff record (newest first) — shown on the Edit screen. */
export const StaffHistory = ({ staffId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    staffMasterApi.history(staffId).then(setRows).catch(() => setRows([]));
  }, [staffId]);
  return (
    <div className="rounded-lg border bg-gray-50/60 p-3" data-testid="staff-history">
      <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><History className="w-4 h-4 text-[#2D5F4F]" />Change History</h4>
      {!rows && <Loader2 className="w-4 h-4 animate-spin my-3 text-gray-400" />}
      {rows && rows.length === 0 && <p className="text-xs text-gray-500 mt-2" data-testid="staff-history-empty">No changes recorded yet (history starts from today's update).</p>}
      {rows && rows.length > 0 && <div className="max-h-56 overflow-y-auto pr-1 mt-1">{rows.map((h, i) => <Entry key={h.log_id} h={h} i={i} />)}</div>}
    </div>
  );
};

export default StaffHistory;
